import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AVAILABLE_WIDGETS, DashboardWidgetType } from '../../types/dashboard';
import { getAllUsersWidgets, updateUserWidgetsByAdmin } from '../../services/dashboardService';

interface UserWidgets {
  _id: string;
  fullName: string;
  username: string;
  email: string;
  dashboardWidgets: string[];
  isActive: boolean;
}

const AdminUserWidgetsPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserWidgets[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWidgets | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWidgets, setSelectedWidgets] = useState<DashboardWidgetType[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getAllUsersWidgets();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'فشل في تحميل بيانات المستخدمين');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: UserWidgets) => {
    setSelectedUser(user);
    setSelectedWidgets(user.dashboardWidgets as DashboardWidgetType[]);
    setDialogOpen(true);
  };

  const handleToggleWidget = (widgetId: DashboardWidgetType) => {
    setSelectedWidgets(prev => {
      if (prev.includes(widgetId)) {
        return prev.filter(id => id !== widgetId);
      } else {
        return [...prev, widgetId];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      await updateUserWidgetsByAdmin(selectedUser._id, selectedWidgets);
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u._id === selectedUser._id 
          ? { ...u, dashboardWidgets: selectedWidgets }
          : u
      ));
      
      setDialogOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(err.message || 'فشل في حفظ التغييرات');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const getWidgetName = (widgetId: string) => {
    const widget = AVAILABLE_WIDGETS.find(w => w.id === widgetId);
    return widget ? widget.title : widgetId;
  };

  const getWidgetIcon = (widgetId: string) => {
    const widget = AVAILABLE_WIDGETS.find(w => w.id === widgetId);
    return widget ? widget.icon : '📊';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              👥 إدارة ويدجات المستخدمين
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              تحكم في الويدجات المتاحة لكل مستخدم
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            العودة
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>المستخدم</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>اسم المستخدم</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>البريد الإلكتروني</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>الويدجات المفعلة</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    لا يوجد مستخدمين
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>{user.fullName}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {user.dashboardWidgets.length === 0 ? (
                        <Chip label="لا يوجد" size="small" />
                      ) : (
                        user.dashboardWidgets.map((widgetId) => (
                          <Chip
                            key={widgetId}
                            label={`${getWidgetIcon(widgetId)} ${getWidgetName(widgetId)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'نشط' : 'غير نشط'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleEditUser(user)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            تعديل ويدجات المستخدم: {selectedUser?.fullName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              اختر الويدجات التي تريد تفعيلها لهذا المستخدم:
            </Typography>
            {AVAILABLE_WIDGETS.map((widget) => (
              <FormControlLabel
                key={widget.id}
                control={
                  <Checkbox
                    checked={selectedWidgets.includes(widget.id)}
                    onChange={() => handleToggleWidget(widget.id)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{widget.icon}</Typography>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {widget.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {widget.description}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ display: 'block', mb: 2 }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || selectedWidgets.length === 0}
          >
            {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserWidgetsPage;